import { useEffect, useRef, useState } from "react";

import { getProxyUrl } from "@/utils/getProxyUrl";

/**
 * Fetches an image URL with CORS mode to ensure sec-fetch-mode: cors header is set.
 * Automatically proxies external URLs through the pricing API image proxy to prevent IP leakage.
 * Converts the fetched image to a blob URL for use in img tags.
 *
 * @param url - The image URL to fetch (can be undefined). Will be proxied if it's an http/https URL.
 * @returns The blob URL if fetch succeeds, otherwise falls back to the proxied URL
 */
export const useCorsImageUrl = (url: string | undefined): string | undefined => {
  const [blobUrl, setBlobUrl] = useState<string | undefined>(undefined);
  const blobUrlRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    // Revoke any existing blob URL before starting a new fetch
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = undefined;
    }

    if (!url) {
      setBlobUrl(undefined);

      return;
    }

    const proxiedUrl = getProxyUrl(url);

    if (!proxiedUrl) {
      setBlobUrl(undefined);

      return;
    }

    // TypeScript doesn't narrow proxiedUrl in the closure, but we've already checked it above
    const urlToFetch: string = proxiedUrl;
    let isCancelled = false;

    const fetchImage = async () => {
      try {
        const response = await fetch(urlToFetch, {
          mode: "cors",
        });

        if (!response.ok) {
          return;
        }

        const blob = await response.blob();

        if (!isCancelled) {
          const objectUrl = URL.createObjectURL(blob);

          blobUrlRef.current = objectUrl;
          setBlobUrl(objectUrl);
        }
      } catch (_error) {
        // Fallback to proxied URL if fetch fails
        if (!isCancelled) {
          setBlobUrl(urlToFetch);
        }
      }
    };

    void fetchImage();

    return () => {
      isCancelled = true;

      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = undefined;
      }
    };
  }, [url]);

  return blobUrl || getProxyUrl(url);
};
